import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpInterceptor, HttpHeaders } from "@angular/common/http";
import { exhaustMap, take } from "rxjs/operators";
import { AuthService } from "src/app/shared/services/auth.service";
import { Helper } from "../helpers/helper";
import { LanguageService } from "../services/language.service";

@Injectable()
export class ReqInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService, private helper: Helper, private languageService: LanguageService) {}

  myHeaders!: HttpHeaders;

  /**
   * Intercepts HTTP requests to add authentication and language headers.
   * Retrieves the JWT token from the AuthService or localStorage, and appends it to the request headers.
   * Also appends the current language code to the request headers if available.
   *
   * @param req The incoming HTTP request object.
   * @param next The HTTP handler to process the request.
   * @returns An observable that emits the handled request.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): any {
    return this.authService.loginSubject.pipe(
      take(1),
      exhaustMap((admin) => {
        const currentLanguageCode = localStorage.getItem("theme_lang");
        const currentLanguage = this.languageService.supportedLanguages.find((l) => l.code === currentLanguageCode);

        let adminToken: any = admin;

        if (!adminToken && localStorage.getItem("userToken")) {
          adminToken = localStorage.getItem("userToken");
        }

        // Signing in must ALWAYS present the DEVICE_INFO token, never a session token.
        //
        // The server guards /api/auth/signin with authMiddleware(Entity.DEVICE_INFO) and
        // rejects any other entity type with 403 "Unauthorized access" — before it ever
        // looks at the email and password. So once a stale `userToken` was left in
        // localStorage (expired session, a previous user, a half-finished login), this
        // interceptor attached it to the sign-in request and the account could never log
        // in again from that browser. Clearing site data was the only way out.
        //
        // Scoped to the sign-in endpoint on purpose: every other request legitimately
        // carries the session token.
        if (req.url.includes("/auth/signin")) {
          adminToken = null;
        }

        if (!adminToken) {
          const jwtToken = this.helper.loginToken;
          if (jwtToken) {
            this.myHeaders = new HttpHeaders().append("type", this.helper.UserType.DISPATCHER.toString()).append("Authorization", jwtToken.toString());
          } else {
            this.myHeaders = new HttpHeaders().append("type", this.helper.UserType.DISPATCHER.toString());
          }
        } else {
          const jwtToken: any = this.helper.userToken ? this.helper.userToken : "";
          this.myHeaders = new HttpHeaders().append("Authorization", jwtToken.toString());
        }

        // Conditionally append language header
        if (currentLanguage) {
          this.myHeaders = this.myHeaders.append("language", currentLanguage.code);
        }

        const modifiedRequest = req.clone({
          headers: this.myHeaders
        });

        return next.handle(modifiedRequest);
      })
    );
  }
}
